import { lazy } from 'react'

/**
 * Chunk-load-failure recovery for React.lazy dynamic imports.
 *
 * Each Vercel deploy renames hashed chunks and DELETES the old ones, so any
 * dashboard session opened before the deploy 404s on its next lazy route
 * navigation ("Failed to fetch dynamically imported module") and the route is
 * dead until a manual reload. The root ErrorBoundary's auto-reload uses ONE
 * GLOBAL sessionStorage guard, so a second stale-chunk failure within its 10s
 * window lands on the dead "Refresh Now" screen.
 *
 * Recovery ladder, PER CHUNK:
 *   1. retry the import once after a short delay (transient network blips);
 *   2. reload the page ONCE (sessionStorage flag keyed by chunk name — a
 *      fresh index.html references the new chunk names, which fixes the
 *      stale-deploy case);
 *   3. if the chunk STILL fails after a reload, rethrow → ErrorBoundary.
 * A successful import clears the chunk's flag so a future deploy gets its
 * own single reload.
 */
export const CHUNK_RELOAD_PREFIX = 'chunk_retry_reload:'

export async function importWithRetry(importer, chunkName, deps = {}) {
  const {
    storage = window.sessionStorage,
    reload = () => window.location.reload(),
    sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
    retryDelayMs = 1000,
  } = deps
  const flagKey = CHUNK_RELOAD_PREFIX + chunkName

  try {
    const mod = await importer()
    storage.removeItem(flagKey)
    return mod
  } catch {
    await sleep(retryDelayMs)
  }

  try {
    const mod = await importer()
    storage.removeItem(flagKey)
    return mod
  } catch (retryErr) {
    if (!storage.getItem(flagKey)) {
      storage.setItem(flagKey, String(Date.now()))
      reload()
      // Never resolve: Suspense keeps showing the loader while the reload
      // tears the page down, instead of flashing the error screen.
      return new Promise(() => {})
    }
    throw retryErr
  }
}

export function lazyWithRetry(importer, chunkName) {
  return lazy(() => importWithRetry(importer, chunkName))
}
